import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { AxiosError } from 'axios'
import React, { useContext } from 'react'
import { Link } from 'react-router-dom'

import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import { EditGoldenSignalContext } from 'components/services/create/EditGoldenSignalContext'
import { SaveServiceGoldenSignal } from 'components/services/create/SaveServiceGoldenSignal'
import { DeleteServiceGoldenSignal } from 'components/services/DeleteServiceGoldenSignal'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { ServiceGoldenSignal as ServiceGoldenServiceType } from 'types/api/services/golden-signal'
import { messageFromResponseError } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

import { SelectTeamExternalIntegration } from './SelectTeamExternalIntegration'

export const ServiceGoldenSignal: React.FC<{
  editMode?: boolean
  onChanged?: (goldenSignals: ServiceGoldenServiceType[]) => void
}> = ({ onChanged: onServiceStatusCheckChanged, editMode }) => {
  const { currentTeamId } = useAuth()
  const {
    integration,
    setIntegration,
    goldenSignals,
    hasGoldenSignalFetchError,
    goldenSignalFetchError,
    isGoldenSignalFetchLoading,
    reFetchGoldenSignals
  } = useContext(EditGoldenSignalContext)

  /**
   * Will annotate, in parentheses, the integration name of the Golden Signal if
   * the name is not a Custom Type.
   * @param goldenSignal
   */
  const getGoldenSignalName = (goldenSignal: ServiceGoldenServiceType) => {
    return `${goldenSignal.name} ${
      goldenSignal.integration.type !== IntegrationType.Custom
        ? ` (${goldenSignal.integration.name})`
        : ''
    }`
  }

  return (
    <PageSection
      errorMessage={messageFromResponseError(
        goldenSignalFetchError as AxiosError,
        'There was an issue while retrieving the Golden Signals',
        {
          500: 'There was an issue while retrieving the Golden Signals'
        }
      )}
      hasError={hasGoldenSignalFetchError}
      isLoading={isGoldenSignalFetchLoading}
    >
      <Stack alignItems="stretch" spacing={2}>
        <Typography data-test-service-golden-signal-description-line1>
          Gremlin will work with your existing observability and monitoring
          tools. We recommend creating alerts and monitors based on the{' '}
          <Link
            rel="noopener"
            target="_blank"
            to={{
              pathname: 'https://www.gremlin.com/blog/four-golden-signals/ '
            }}
          >
            Golden Signals
            <Icon
              css={{
                fontSize: theme.fontSizes[2],
                marginLeft: theme.space[1]
              }}
              icon="arrowUpRightFromSquare"
            />
          </Link>{' '}
          . Each Service will require their own set of Golden Signal monitors
          and alerts. However, once one team member authenticates, that
          authentication will be shared in{' '}
          <Link
            target="_blank"
            to={`/settings/team/${currentTeamId}/teamIntegrations`}
          >
            {' '}
            Team Settings{' '}
          </Link>{' '}
          . Learn how to add{' '}
          <Link
            rel="noopener"
            target="_blank"
            to={{
              pathname:
                'https://www.gremlin.com/docs/reliability-management/golden-signals/'
            }}
          >
            Golden Signals
            <Icon
              css={{
                fontSize: theme.fontSizes[2],
                marginLeft: theme.space[1]
              }}
              icon="arrowUpRightFromSquare"
            />
          </Link>{' '}
          to your Service.
        </Typography>
        <Typography data-test-service-golden-signal-description-line2>
          Once connected, your Golden Signal monitor will help Gremlin determine
          how well your Services are weathering Reliability Tests. When Gremlin
          sees your monitor alerting, Reliability Tests will be automatically
          halted for this Service.
        </Typography>
        <Stack alignItems="center" direction="row" spacing={2}>
          <SelectTeamExternalIntegration
            integrationName={integration?.name}
            onChanged={setIntegration}
          />
          <SaveServiceGoldenSignal
            disabled={!integration}
            onChanged={async (goldenSignal) => {
              onServiceStatusCheckChanged &&
                onServiceStatusCheckChanged([...goldenSignals, goldenSignal])
            }}
          />
        </Stack>

        {goldenSignals && goldenSignals.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Golden Signal Name</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {goldenSignals.map((s) => (
                  <TableRow key={s.name}>
                    <TableCell
                      css={{ overflowWrap: 'anywhere' }}
                      data-test-service-status-check={s.name}
                    >
                      {getGoldenSignalName(s)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2}>
                        <SaveServiceGoldenSignal
                          goldenSignal={s}
                          onChanged={async (goldenSignal) => {
                            const goldenSignalIndex = goldenSignals.findIndex(
                              (s) => s.id === goldenSignal.id
                            )
                            if (goldenSignalIndex !== -1) {
                              goldenSignals[goldenSignalIndex] = goldenSignal
                              onServiceStatusCheckChanged([...goldenSignals])
                            }
                            await reFetchGoldenSignals()
                          }}
                        />
                        <DeleteServiceGoldenSignal
                          disabled={editMode && goldenSignals?.length === 1}
                          goldenSignalId={s.id}
                          goldenSignalName={s.name}
                          onChanged={async () => {
                            const newStatusChecks = goldenSignals.filter(
                              (st) => st.id !== s.id
                            )
                            onServiceStatusCheckChanged(newStatusChecks)
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </PageSection>
  )
}
